import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

const csrfTokenTag = document.querySelector('meta[name="csrf-token"]');

if (csrfTokenTag) {
  axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfTokenTag.getAttribute('content') || '';
}

export function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';
}

function snakecaseBody(body) {
  if (!body) return null;
  // FormData is not a plain object and isn't enumerable via Object.keys, so snakecase-keys
  // walks an empty key list and returns `{}` - silently discarding multipart payloads.
  // Pass it through unchanged; axios handles the multipart serialization on its own.
  if (body instanceof FormData) return body;

  return snakecaseKeys(body, { exclude: [/\[|\]|]|_destroy/] });
}

function snakecaseParams(options) {
  if (options.params) {
    options = { ...options, params: snakecaseBody(options.params) };
  }

  return options;
}

function makeRequest(method, ...args) {
  const instance = defaultAxiosInstance();

  return instance[method](...args)
    .then((response) => {
      try {
        return camelcaseKeys(response.data, { deep: true });
      } catch (error) {
        return response.data;
      }
    })
    .catch((error) => {
      let errors;
      if (error.response) {
        errors = error.response.data.errors;
      }

      throw errors || error;
    });
}

export function put(path, body, options = {}): Promise<any> {
  return makeRequest('put', path, snakecaseBody(body), options);
}

export function post(path, body?, options = {}): Promise<any> {
  return makeRequest('post', path, snakecaseBody(body), options);
}

export function patch(path, body, options = {}): Promise<any> {
  return makeRequest('patch', path, snakecaseBody(body), options);
}

export function get(path, options = {}): Promise<any> {
  return makeRequest('get', path, snakecaseParams(options));
}

// Like `get` but returns the response body verbatim — no camelCase conversion. Use when the
// payload has keys that must survive as-is (e.g. hyphenated CSS style keys).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getRaw(path, options = {}): Promise<any> {
  return defaultAxiosInstance().get(path, options).then(response => response.data);
}

export function deleteRequest(path, options = {}): Promise<any> {
  return makeRequest('delete', path, snakecaseParams(options));
}

export function defaultAxiosInstance() {
  return axios.create({
    // This is required for `request.xhr?` to return true in Rails-land
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  });
}
